/**
 * Control State Actions Component
 * @implements FS-002.6 - Project Control States
 * @implements TS-075 - Control State UI Components
 *
 * Renders contextual action buttons based on the current control state:
 * - NORMAL: "Enter Draft Mode" button
 * - DRAFT: "Submit for Review" and "Discard Changes" buttons
 * - PENDING: "Approve Changes" and "Request Revisions" buttons
 */

import { useState } from 'react';
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Alert,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DiscardDraftDialog } from './DiscardDraftDialog';
import { type UseProjectControlStateResult } from '@/hooks/useProjectControlState';

/**
 * Props for ControlStateActions component
 */
export interface ControlStateActionsProps {
  /** Control state hook result */
  controlState: UseProjectControlStateResult;
  /** Whether to show action buttons inline or as a button group */
  variant?: 'inline' | 'group';
  /** Size of the buttons */
  size?: 'small' | 'medium' | 'large';
  /** Whether to show icons on buttons */
  showIcons?: boolean;
  /** Callback when an action starts */
  onActionStart?: () => void;
  /** Callback when an action completes successfully */
  onActionComplete?: () => void;
  /** Callback when an action fails */
  onActionError?: (error: string) => void;
}

/**
 * Control State Actions Component
 * @implements TS-075 - Control State UI Components
 *
 * Displays contextual action buttons based on current control state.
 * Handles loading states, error display, and confirmation dialogs.
 *
 * @example
 * ```tsx
 * const controlState = useProjectControlState({
 *   projectId: project.id,
 *   pmValidated: project.pmValidated,
 *   poValidated: project.poValidated,
 *   controlState: project.controlState,
 * });
 *
 * <ControlStateActions
 *   controlState={controlState}
 *   variant="inline"
 *   showIcons
 * />
 * ```
 */
export const ControlStateActions = ({
  controlState,
  variant = 'inline',
  size = 'medium',
  showIcons = true,
  onActionStart,
  onActionComplete,
  onActionError,
}: ControlStateActionsProps) => {
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const {
    isLoading,
    error,
    canEnterDraftMode,
    canSubmitForReview,
    canApproveChanges,
    canRejectChanges,
    canDiscardDraft,
    enterDraftMode,
    submitForReview,
    approveChanges,
    rejectChanges,
    discardDraft,
    isNormal,
    isDraft,
    isPending,
  } = controlState;

  /**
   * Wrap action handlers with callbacks
   */
  const handleAction = async (action: () => Promise<void>) => {
    onActionStart?.();
    try {
      await action();
      onActionComplete?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Action failed';
      onActionError?.(message);
    }
  };

  /**
   * Handle discard draft with confirmation dialog
   */
  const handleDiscardDraft = () => {
    setDiscardDialogOpen(true);
  };

  const confirmDiscardDraft = async () => {
    setDiscardDialogOpen(false);
    await handleAction(discardDraft);
  };

  /**
   * Render buttons for NORMAL state
   */
  const renderNormalStateActions = () => (
    <Tooltip title="Start making changes to the project">
      <span>
        <Button
          variant="outlined"
          color="primary"
          size={size}
          disabled={!canEnterDraftMode}
          onClick={() => handleAction(enterDraftMode)}
          startIcon={showIcons && !isLoading ? <EditIcon /> : undefined}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Enter Draft Mode'
          )}
        </Button>
      </span>
    </Tooltip>
  );

  /**
   * Render buttons for DRAFT state
   */
  const renderDraftStateActions = () => (
    <>
      <Tooltip title="Submit changes for Product Owner review">
        <span>
          <Button
            variant="contained"
            color="primary"
            size={size}
            disabled={!canSubmitForReview}
            onClick={() => handleAction(submitForReview)}
            startIcon={showIcons && !isLoading ? <SendIcon /> : undefined}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Submit for Review'
            )}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Discard all draft changes and return to normal state">
        <span>
          <Button
            variant="outlined"
            color="error"
            size={size}
            disabled={!canDiscardDraft}
            onClick={handleDiscardDraft}
            startIcon={showIcons ? <DeleteIcon /> : undefined}
          >
            Discard Changes
          </Button>
        </span>
      </Tooltip>
    </>
  );

  /**
   * Render buttons for PENDING state
   */
  const renderPendingStateActions = () => (
    <>
      <Tooltip title="Approve the submitted changes">
        <span>
          <Button
            variant="contained"
            color="success"
            size={size}
            disabled={!canApproveChanges}
            onClick={() => handleAction(approveChanges)}
            startIcon={showIcons && !isLoading ? <CheckIcon /> : undefined}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Approve Changes'
            )}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Request revisions and return to draft mode">
        <span>
          <Button
            variant="outlined"
            color="warning"
            size={size}
            disabled={!canRejectChanges}
            onClick={() => handleAction(rejectChanges)}
            startIcon={showIcons ? <UndoIcon /> : undefined}
          >
            Request Revisions
          </Button>
        </span>
      </Tooltip>
    </>
  );

  /**
   * Render action buttons based on current state
   */
  const renderActions = () => {
    if (isNormal) {
      return renderNormalStateActions();
    }
    if (isDraft) {
      return renderDraftStateActions();
    }
    if (isPending) {
      return renderPendingStateActions();
    }
    return null;
  };

  const actions = renderActions();

  if (!actions) {
    return null;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {variant === 'group' ? (
        <ButtonGroup variant="outlined" size={size}>
          {actions}
        </ButtonGroup>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>
      )}

      <DiscardDraftDialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        onConfirm={confirmDiscardDraft}
        isLoading={isLoading}
      />
    </Box>
  );
};
